import { useState, useEffect } from "react";
import type { TimeEntry } from "../lib/detectDuplicates";

export interface TeamworkUser {
  id: string;
  "first-name": string;
  "last-name": string;
  avatar_url?: string;
}

function formatDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}${m}${day}`;
}

export function useTeamworkUsers() {
  const [users, setUsers] = useState<TeamworkUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetch("/api/teamwork/users")
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json() as Promise<{ people: TeamworkUser[] }>;
      })
      .then((data) => {
        const sorted = [...(data.people ?? [])].sort((a, b) =>
          `${a["first-name"]} ${a["last-name"]}`.localeCompare(
            `${b["first-name"]} ${b["last-name"]}`,
          ),
        );
        setUsers(sorted);
      })
      .catch((err: Error) => setError(err.message))
      .finally(() => setLoading(false));
  }, []);

  return { users, loading, error };
}

interface UseTimesheetsArgs {
  userIds: string[];
  fromDate: Date;
  toDate: Date;
}

export function useTimesheets({
  userIds,
  fromDate,
  toDate,
}: UseTimesheetsArgs) {
  const [entries, setEntries] = useState<TimeEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const key = `${userIds.join(",")}|${formatDate(fromDate)}|${formatDate(toDate)}`;

  useEffect(() => {
    if (userIds.length === 0) {
      setEntries([]);
      return;
    }
    setLoading(true);
    setError(null);

    Promise.all(
      userIds.map((uid) =>
        fetch(
          `/api/teamwork/time-entries?fromdate=${formatDate(fromDate)}&todate=${formatDate(toDate)}&userId=${uid}`,
        )
          .then((r) => {
            if (!r.ok) throw new Error(`HTTP ${r.status}`);
            return r.json() as Promise<{ "time-entries": TimeEntry[] }>;
          })
          .then((data) => data["time-entries"] ?? []),
      ),
    )
      .then((results) => setEntries(results.flat()))
      .catch((err: Error) => setError(err.message))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  return { entries, loading, error };
}
